import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Group, Layout, Page, Tab } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useTab, useResource, usePage, useStore } from "@/comn/hooks";
import { APIS, SF_ORG_MGMT_INFO, SF_ORG_DEPT_INFO, URLS } from "@/tz/go/egaz/osci/ptl/usr/org/services/OsciOrgService";

/*
 * registration for Organization Management
 * !== 기관 관리 등록 ==!
 */
export const OSCI0103006S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    const { updateResource } = useResource({
        // 등록 성공 후 갱신하는 기관 코드 리소스를 화면 수명주기에도 명시적으로 등록한다.
        defaultSchema: [{ area: "orgDeptCd" }, { area: "orgCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgInfo: useForm({
            defaultSchema: SF_ORG_MGMT_INFO,
            defaultValues: {},
        }),
        // Organization Information Form !== 기관의 부서 정보 form ==!
        deptInfo: useForm({
            defaultSchema: SF_ORG_DEPT_INFO,
            defaultValues: {},
        }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_ORG" }, { label: "L_ORG_DEPT" }],
        },
    });

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Organization Information !== 기관 정보 등록 ==!
        createOrgInfo: useFetch({
            api: (data: any) => APIS.createOrgInfo(data),
            enabled: false,
            onSuccess: async () => {
                // 신규 OGA가 다른 화면의 Organization Code 자동완성에 즉시 표시되도록 공통 리소스를 갱신한다.
                await updateResource("orgCd");
                modal.openModal({
                    content: "msg.osc.00069",
                    onConfirm: () => {
                        navigate(URLS.osci0103005q);
                    },
                    close: false,
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        // Check Organization Information !== 기관 정보 조회 ==!
        getOrgInfo: useFetch({
            api: (data) => APIS.getOrgInfo(form.orgInfo.getValue("orgCd")),
            onSuccess: (data) => {
                const orgInfo = data.orgInfo.content;
                form.orgInfo.setValues(orgInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),

        //Organization Tin duplicate check !== 기관 TIN 중복 체크 ==!
        getOrgTinDuplCheck: useFetch({
            api: (tin: string) => APIS.getOrgTinDuplCheck(tin),
            enabled: false,
            onError: () => {
                toast.showToast({ type: "error", content: "msg.osc.00035" });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        initDeptForm: () => {
            form.deptInfo.clearValues();
            form.deptInfo.setValue("deptPath", "ROOT");
        },
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => {
            navigate(URLS.osci0103005q);
        },
        // Click Organization Save Button !== 기관 저장 버튼 클릭 ==!
        onOrgSaveBtnClick: async () => {
            //fetch.getOrgInfo.fetch();
            const regex = /^\d{9}$/; // TIN을 체크하는 정규식 1. 숫자로만 이루어짐, 2. 9자리수로 이루어져야함
            const data = form.orgInfo.getValues();

            // 정규화를 통한 TIN 조건 처리
            if (!regex.test(form.orgInfo.getValue("tin"))) {
                modal.openModal({
                    content: "msg.osc.00076", // TIN must be 9 digits. ? !== TIN은 9자리여야 합니다. ? ==!
                });
                return;
            }

            // TIN 중복 여부 처리
            const resp = await APIS.getOrgTinDuplCheck(form.orgInfo.getValue("tin"));
            const isDup = resp.data.orgTinDuplCheck;

            if (isDup.content) {
                modal.openModal({ content: "msg.osc.00232" }); // This TIN number is already in use. ? !== 이미 사용 중인 TIN 번호입니다. ? ==!
                return;
            }
            // save Information of Organization !== 기관 정보 저장 ==!
            form.orgInfo.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.createOrgInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Click Department Tab 부서 탭 클릭
        onDeptTabClick: (index: any) => {
            form.deptInfo.setEditable(false);
            if (index === 1) {
                modal.openModal({
                    content: t("msg.osc.00144", {
                        0: t("저장하지 않은 데이터가 존재합니다. 먼저, 저장하고 다시 시도해주시기 바랍니다."),
                    }),
                });
                tab.setActive(0);
                return;
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        // New OSC organizations belong to Zanzibar, so the region is fixed instead of being user-selectable.
        // MAIN LAND(TANZANIA) 선택지는 등록 화면에서만 제외한다. (기존 MAIN LAND 기관 조회/수정에는 필요)
        // 조회 모드(edit:false)에서는 옵션 label 이 번역되지 않으므로 t() 로 변환한 문구를 넣는다.
        form.orgInfo.setValue("orgRegnDvnCd", "Z");
        form.orgInfo.setSchema("orgRegnDvnCd", {
            edit: false,
            viewType: "label",
            options: [{ label: t("L_REGN_Z_CD"), value: "Z" }],
        });
        form.deptInfo.setEditable(true);
        handler.initDeptForm();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.orgRegnDvnCd}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.orgAbrtNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.orgNm}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.tin}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.hgovNm}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.orgStatCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.rprsTelno}></Group.Control>
                            <Group.Control {...form.orgInfo.schema.rprsFaxNo}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.lpcoIssuYn}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgInfo.schema.postAddr}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onOrgSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
